import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Fade,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    wrapper: {
      borderRadius: 18,
      padding: 0,
      background: dark
        ? "linear-gradient(145deg, rgba(18,18,19,0.85) 0%, rgba(26,26,28,0.85) 100%)"
        : "#ffffff",
      border: dark
        ? "1px solid rgba(91,124,255,0.14)"
        : "1px solid #e2e8f0",
      boxShadow: dark
        ? "0 8px 32px rgba(0,0,0,0.4)"
        : "0 4px 6px rgba(0,0,0,0.05)",
      overflow: "hidden",
    },
    tableScroll: {
      overflowX: "auto",
      ...theme.scrollbarStylesSoft,
    },
    header: {
      padding: "16px 24px 12px",
      borderBottom: dark
        ? "1px solid rgba(255,255,255,0.05)"
        : "1px solid #f1f5f9",
    },
    title: {
      fontSize: 14,
      fontWeight: 700,
      letterSpacing: "-0.01em",
      color: dark ? "#fff" : "#0f172a",
    },
    subtitle: {
      fontSize: 11,
      color: dark ? "rgba(255,255,255,0.42)" : "#94a3b8",
      marginTop: 2,
    },
    th: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.5)" : "#64748b",
      borderBottom: dark
        ? "1px solid rgba(255,255,255,0.05)"
        : "1px solid #f1f5f9",
      padding: "10px 16px",
    },
    td: {
      fontSize: 13,
      color: dark ? "rgba(255,255,255,0.85)" : "#1e293b",
      borderBottom: dark
        ? "1px solid rgba(255,255,255,0.04)"
        : "1px solid #f8fafc",
      padding: "12px 16px",
    },
    row: {
      transition: "background 140ms ease",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.025)" : "#f8fafc",
      },
    },
    providerChip: {
      height: 22,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
    },
    wahaChip: {
      background: "rgba(34,211,238,0.12)",
      color: "#22d3ee",
      border: "1px solid rgba(34,211,238,0.25)",
    },
    metaChip: {
      background: "rgba(167,139,250,0.12)",
      color: "#a78bfa",
      border: "1px solid rgba(167,139,250,0.25)",
    },
    numCell: {
      fontVariantNumeric: "tabular-nums",
      fontWeight: 600,
      textAlign: "right",
    },
    rateCell: {
      fontVariantNumeric: "tabular-nums",
      fontWeight: 600,
      textAlign: "right",
      color: dark ? "rgba(255,255,255,0.78)" : "#334155",
    },
    rateMuted: {
      color: dark ? "rgba(255,255,255,0.32)" : "#cbd5e1",
      fontWeight: 500,
    },
    rateGood: { color: dark ? "#86efac" : "#16a34a" },
    rateBad: { color: dark ? "#fca5a5" : "#dc2626" },
    empty: {
      padding: 32,
      textAlign: "center",
      fontSize: 12,
      color: dark ? "rgba(255,255,255,0.4)" : "#94a3b8",
    },
  };
});

const ALL_COLUMNS = [
  { id: "companyName", label: "Empresa", sortable: true },
  { id: "name", label: "Conexão", sortable: true },
  { id: "provider", label: "Provider", sortable: true },
  { id: "sent", label: "Enviadas", sortable: true, numeric: true },
  { id: "received", label: "Recebidas", sortable: true, numeric: true },
  { id: "total", label: "Total", sortable: true, numeric: true },
  { id: "uniqueContacts", label: "Conversas únicas", sortable: true, numeric: true },
  { id: "readRate", label: "Leitura", sortable: true, numeric: true },
  { id: "deliveryRate", label: "Entrega", sortable: true, numeric: true },
  { id: "failureRate", label: "Falha", sortable: true, numeric: true },
];

const providerLabel = (p) => {
  if (p === "waha") return "Não oficial";
  if (p === "meta-api") return "API Oficial";
  return (p || "").toUpperCase();
};

// Heurística de cor: leitura/entrega altas = bom; falha alta = ruim.
// Thresholds conservadores pra não pintar tudo de vermelho/verde.
const colorClassFor = (kind, value, classes) => {
  if (value == null) return classes.rateMuted;
  if (kind === "failed") {
    if (value >= 5) return classes.rateBad;
    return "";
  }
  if (value >= 80) return classes.rateGood;
  if (value < 50) return classes.rateBad;
  return "";
};

const RateCell = ({ value, classes, kind }) => (
  <TableCell className={`${classes.td} ${classes.rateCell} ${colorClassFor(kind, value, classes)}`} align="right">
    {value == null ? "—" : `${value.toFixed(1)}%`}
  </TableCell>
);

const ConnectionsTable = ({ rows = [], hideCompanyColumn = false }) => {
  const classes = useStyles();
  const [orderBy, setOrderBy] = useState("total");
  const [order, setOrder] = useState("desc");

  // Quando todas as linhas pertencem à mesma empresa (visão per-tenant), a
  // coluna "Empresa" só ocupa espaço sem informar nada novo.
  const COLUMNS = useMemo(
    () => (hideCompanyColumn ? ALL_COLUMNS.filter(c => c.id !== "companyName") : ALL_COLUMNS),
    [hideCompanyColumn]
  );

  // Pré-deriva as taxas pra suportar sort sem recalcular no comparator.
  // Sem envios → null (renderizado como "—" e ordenado pro fim).
  const enriched = useMemo(() => {
    return rows.map(r => {
      const sent = r.sent || 0;
      const safe = (n) => (sent > 0 ? (n / sent) * 100 : null);
      return {
        ...r,
        readRate: safe(r.read || 0),
        deliveryRate: safe(r.delivered || 0),
        failureRate: safe(r.failed || 0),
      };
    });
  }, [rows]);

  const sortedRows = useMemo(() => {
    const arr = [...enriched];
    arr.sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      // null vai sempre pro fim, independente da direção.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return order === "asc" ? av - bv : bv - av;
      }
      const as = String(av || "").toLowerCase();
      const bs = String(bv || "").toLowerCase();
      return order === "asc" ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return arr;
  }, [enriched, orderBy, order]);

  const handleSort = (col) => {
    if (orderBy === col) setOrder(order === "asc" ? "desc" : "asc");
    else { setOrderBy(col); setOrder("desc"); }
  };

  return (
    <Fade in timeout={500} style={{ transitionDelay: "350ms" }}>
      <Box className={classes.wrapper}>
        <Box className={classes.header}>
          <Typography component="div" className={classes.title}>Por conexão</Typography>
          <Typography component="div" className={classes.subtitle}>
            {rows.length} conexão{rows.length === 1 ? "" : "ões"} no período
          </Typography>
        </Box>
        {rows.length === 0 ? (
          <Box className={classes.empty}>Nenhuma mensagem no período.</Box>
        ) : (
          <Box className={classes.tableScroll}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map(col => (
                  <TableCell
                    key={col.id}
                    className={classes.th}
                    align={col.numeric ? "right" : "left"}
                  >
                    {col.sortable ? (
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : "desc"}
                        onClick={() => handleSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map(r => (
                <TableRow key={`${r.whatsappId}-${r.provider}`} className={classes.row}>
                  {!hideCompanyColumn && (
                    <TableCell className={classes.td}>{r.companyName}</TableCell>
                  )}
                  <TableCell className={classes.td}>{r.name}</TableCell>
                  <TableCell className={classes.td}>
                    <Chip
                      size="small"
                      label={providerLabel(r.provider)}
                      className={`${classes.providerChip} ${r.provider === "meta-api" ? classes.metaChip : classes.wahaChip}`}
                    />
                  </TableCell>
                  <TableCell className={`${classes.td} ${classes.numCell}`} align="right">
                    {r.sent.toLocaleString("pt-BR")}
                  </TableCell>
                  <TableCell className={`${classes.td} ${classes.numCell}`} align="right">
                    {r.received.toLocaleString("pt-BR")}
                  </TableCell>
                  <TableCell className={`${classes.td} ${classes.numCell}`} align="right">
                    {r.total.toLocaleString("pt-BR")}
                  </TableCell>
                  <TableCell className={`${classes.td} ${classes.numCell}`} align="right">
                    {(r.uniqueContacts || 0).toLocaleString("pt-BR")}
                  </TableCell>
                  <RateCell value={r.readRate} classes={classes} kind="read" />
                  <RateCell value={r.deliveryRate} classes={classes} kind="delivered" />
                  <RateCell value={r.failureRate} classes={classes} kind="failed" />
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>
        )}
      </Box>
    </Fade>
  );
};

export default ConnectionsTable;
